import { useZakeke } from "@zakeke/zakeke-configurator-react";
import { T } from "Helpers";
import useStore from "Store";
import styled from "styled-components";
import { ReactComponent as AngleLeftSolid } from "../../assets/icons/angle-left-solid.svg";
import { ReactComponent as QuoteSolid } from "../../assets/icons/dollar-sign-solid.svg";
import { ReactComponent as PdfSolid } from "../../assets/icons/file-pdf-solid.svg";
import { ReactComponent as ShareSolid } from "../../assets/icons/share-alt-square-solid.svg";
import { ReactComponent as CartSolid } from "../../assets/icons/shopping-cart-solid.svg";
import {
  MessageDialog,
  QuestionDialog,
  useDialogManager,
} from "../dialogs/Dialogs";
import ErrorDialog from "../dialogs/ErrorDialog";
import PdfDialog from "../dialogs/PdfDialog";
import ShareDialog from "../dialogs/ShareDialog";
import { FooterMobileContainer, PriceContainer } from "./LayoutStyled";

import QuotationFormDialog from "components/dialogs/QuotationFormDialog";
import SaveDesignsDraftDialog from "components/dialogs/SaveDesignsDraftDialog";
import { TailSpin } from "react-loader-spinner";
import { ReactComponent as SaveSolid } from "../../assets/icons/save-solid.svg";

const FooterMobileIcon = styled.div<{
  isHidden?: boolean;
  color?: string;
  backgroundColor?: string;
  iconColor?: string;
  isCart?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px transparent solid;
  color: ${(props) => (props.color ? props.color : `#313c46`)};
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : `transparent`};
  font-size: 14px;
  text-transform: uppercase;
  display: inline-flex;
  min-height: 38px;
  border: none;
  border-right: 3px #f4f4f4 solid;
  cursor: pointer;
  svg {
    fill: ${(props) => props.iconColor && `${props.iconColor}`};
    width: 32px;
    height: 32px;
  }
  ${(props) => props.isHidden && `visibility:hidden`};
  ${(props) =>
    props.isCart &&
    `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;`};
`;

const FooterMobile = () => {
  const {
    useLegacyScreenshot,
    setCameraByName,
    getPDF,
    addToCart,
    product,
    price,
    groups,
    isSceneLoading,
    eventMessages,
    isAddToCartLoading,
    sellerSettings,
    saveComposition,
    createQuote,
  } = useZakeke();
  const {
    setIsLoading,
    selectedGroupId,
    setSelectedGroupId,
    selectedAttributeId,
    selectedStepId,
    setSelectedAttributeId,
    priceFormatter,
    setIsQuoteLoading,
    isQuoteLoading,
    isViewerMode,
    isDraftEditor,
    isEditorMode,
    isMobile,
    customSizes,
    personalDetailsData,
  } = useStore();
  const { showDialog, closeDialog } = useDialogManager();

  const buyVisibleForQuoteRule = product?.quoteRule
    ? product.quoteRule.allowAddToCart
    : true;

  const handleAddToCart = () => {
    const cartMessage = eventMessages?.find((message) => message.eventID === 4);
    if (cartMessage && cartMessage.visible && !isDraftEditor && !isEditorMode)
      showDialog(
        "question",
        <QuestionDialog
          alignButtons="center"
          eventMessage={cartMessage?.description}
          buttonNoLabel={T._("Cancel", "Composer")}
          buttonYesLabel={T._("Add to cart", "Composer")}
          onYesClick={() => {
            closeDialog("question");
            addToCart(
              [personalDetailsData, customSizes],
              undefined,
              useLegacyScreenshot
            );
          }}
        />
      );
    else {
      addToCart(
        [personalDetailsData, customSizes],
        undefined,
        useLegacyScreenshot
      );
    }
  };

  const showError = (error: string) => {
    showDialog(
      "error",
      <ErrorDialog error={error} onCloseClick={() => closeDialog("error")} />
    );
  };

  const handleShareClick = async () => {
    setCameraByName("buy_screenshot_camera", false, false);
    showDialog("share", <ShareDialog />);
  };
  const handleSaveClick = async () => {
    showDialog(
      "save",
      <SaveDesignsDraftDialog onCloseClick={() => closeDialog("save")} />
    );
  };
  const handlePdfClick = async () => {
    try {
      setIsLoading(true);
      const url = await getPDF();
      showDialog(
        "pdf",
        <PdfDialog url={url} onCloseClick={() => closeDialog("pdf")} />
      );
    } catch (ex) {
      console.log(ex);
      showError(T._("Failed PDF generation", "Composer"));
    } finally {
      setIsLoading(false);
    }
  };
  const handleBackClick = () => {
    if (selectedAttributeId) {
      setSelectedAttributeId(null);

      const selectedCurrentGroup = groups.find((x) => x.id === selectedGroupId);
      const selectedCurrentStep = selectedCurrentGroup?.steps.find(
        (x) => x.id === selectedStepId
      );

      if (
        selectedCurrentGroup &&
        (selectedCurrentGroup.attributes.length === 1 ||
          selectedCurrentStep?.attributes.length === 1)
      ) {
        setSelectedGroupId(null);
      }
    } else if (selectedGroupId) {
      setSelectedGroupId(null);
    }
  };

  const handleSubmitRequestQuote = async (formData: any) => {
    try {
      closeDialog("request-quotation");
      setIsQuoteLoading(true);
      await saveComposition();
      await createQuote(formData);
      showDialog(
        "message",
        <MessageDialog
          message={T._("Request for quotation sent successfully", "Composer")}
        />
      );
      setIsQuoteLoading(false);
    } catch (ex) {
      console.error(ex);
      setIsQuoteLoading(false);
      showDialog(
        "error",
        <ErrorDialog
          error={T._(
            "An error occurred while sending request for quotation. Please try again.",
            "Composer"
          )}
          onCloseClick={() => closeDialog("error")}
        />
      );
    }
  };

  const handleGetQuoteClick = async () => {
    let rule = product?.quoteRule;
    if (rule)
      showDialog(
        "request-quotation",
        <QuotationFormDialog
          onCloseClick={() => closeDialog("request-quotation")}
          getQuoteRule={rule}
          onFormSubmit={handleSubmitRequestQuote}
        />
      );
  };

  return (
    <>
      {!isSceneLoading && (
        <FooterMobileContainer isQuoteEnable={product?.quoteRule !== null}>
          <FooterMobileIcon
            isHidden={selectedGroupId === null}
            onClick={handleBackClick}
          >
            <AngleLeftSolid />
          </FooterMobileIcon>

          {
            <FooterMobileIcon onClick={handlePdfClick}>
              <PdfSolid />
            </FooterMobileIcon>
          }

          {!isDraftEditor &&
            !isEditorMode &&
            sellerSettings &&
            sellerSettings.canSaveDraftComposition && (
              <FooterMobileIcon onClick={handleSaveClick}>
                <SaveSolid />
              </FooterMobileIcon>
            )}

          {!isEditorMode &&
            sellerSettings &&
            sellerSettings.shareType !== 0 && (
              <FooterMobileIcon onClick={handleShareClick}>
                <ShareSolid />
              </FooterMobileIcon>
            )}

          {product?.quoteRule &&
            !isViewerMode &&
            !isDraftEditor &&
            !isEditorMode && (
              <FooterMobileIcon
                iconColor="white"
                color="white"
                backgroundColor="#313c46"
                onClick={handleGetQuoteClick}
              >
                {!isQuoteLoading && <QuoteSolid />}
                {isQuoteLoading && <TailSpin color="#FFFFFF" height="25px" />}
              </FooterMobileIcon>
            )}

          {buyVisibleForQuoteRule && !isViewerMode && (
            <FooterMobileIcon
              isCart
              iconColor="white"
              color="white"
              backgroundColor="#313c46"
              onClick={handleAddToCart}
            >
              {price !== null &&
                price > 0 &&
                (!sellerSettings || !sellerSettings.hidePrice) && (
                  <PriceContainer isMobile={isMobile}>
                    {priceFormatter.format(price)}
                  </PriceContainer>
                )}
              {!isAddToCartLoading &&
                (isDraftEditor || isEditorMode ? <SaveSolid /> : <CartSolid />)}
              {isAddToCartLoading && <TailSpin color="#FFFFFF" height="25px" />}
            </FooterMobileIcon>
          )}
        </FooterMobileContainer>
      )}
    </>
  );
};

export default FooterMobile;
